import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";

const HomepageMapInner = lazy(() => import("./HomepageMapInner"));

/**
 * Viewport-lazy wrapper. Mounts the actual map (and dynamically-imported
 * mapbox-gl bundle) only when the section scrolls near the viewport.
 * Keeps ~800KB of JS + CSS + the listings fetch off the initial homepage load.
 */
export const HomepageMap = () => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView || !sentinelRef.current) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [inView]);

  return (
    <section className="w-full py-8 md:py-12 bg-background" aria-label="Mapa de centros deportivos">
      <div className="container mx-auto px-4">
        <div ref={sentinelRef} />
        {inView ? (
          <Suspense fallback={<MapSkeleton />}>
            <HomepageMapInner />
          </Suspense>
        ) : (
          <MapSkeleton />
        )}
      </div>
    </section>
  );
};

const MapSkeleton = () => (
  <>
    <div className="mb-4 md:mb-6 text-center">
      <h2 className="text-2xl md:text-3xl font-bold mb-1">
        Encuentra centros deportivos cerca de ti
      </h2>
      <p className="text-sm md:text-base text-muted-foreground">
        Cargando centros verificados en el mapa…
      </p>
    </div>
    <div className="relative w-full h-[380px] md:h-[520px] rounded-xl overflow-hidden border border-border shadow-sm bg-muted/30 flex items-center justify-center">
      <div className="flex flex-col items-center gap-2 text-muted-foreground">
        <MapPin className="h-8 w-8" />
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    </div>
  </>
);

export default HomepageMap;
